// DISCLAIMER: Este documento é uma base inicial para fins informativos e NÃO substitui
// assessoria jurídica especializada. Recomenda-se revisão por advogado com expertise em
// proteção de dados (LGPD) antes de publicar em produção.

import { useTranslation } from 'react-i18next';
import { MarketingLayout } from '../marketing/marketing-layout';

const LAST_UPDATED = '29 de junho de 2026';

export function PrivacyPage() {
  const { t } = useTranslation();

  return (
    <MarketingLayout
      title={t('privacy.pageTitle', 'Política de Privacidade')}
      subtitle={t(
        'privacy.pageSubtitle',
        'Como coletamos, usamos e protegemos seus dados — em conformidade com a LGPD (Lei 13.709/2018).'
      )}
    >
      <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8 text-muted-foreground">

        {/* Last updated */}
        <p className="text-sm border border-border rounded-lg px-4 py-3 bg-muted/30 text-muted-foreground">
          <strong className="text-foreground">{t('privacy.lastUpdatedLabel', 'Última atualização:')}</strong> {LAST_UPDATED}
        </p>

        {/* 1 — Identificação do Controlador */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section1Title', '1. Identificação do Controlador')}
          </h2>
          <p>
            {t('privacy.section1Body1', 'O controlador dos seus dados pessoais é o operador da plataforma')}{' '}
            <strong className="text-foreground">reformu.lat</strong>
            {t(
              'privacy.section1Body2',
              ', serviço de escrita de livros com assistência de inteligência artificial. Para questões relacionadas a privacidade e proteção de dados, o encarregado (DPO) pode ser contactado pelo e-mail'
            )}{' '}
            <a href="mailto:diegosierravibes@gmail.com" className="text-primary underline underline-offset-4">
              diegosierravibes@gmail.com
            </a>.
          </p>
        </section>

        {/* 2 — Dados Coletados */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section2Title', '2. Dados que Coletamos e Finalidade')}
          </h2>
          <p>
            {t('privacy.section2Intro', 'Coletamos apenas os dados estritamente necessários para a prestação dos nossos serviços:')}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              <strong className="text-foreground">{t('privacy.section2Item1Label', 'Nome e endereço de e-mail')}</strong>{' '}
              {t('privacy.section2Item1Body', '— para criação de conta, autenticação e comunicação sobre o serviço.')}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section2Item2Label', 'Senha')}</strong>{' '}
              {t(
                'privacy.section2Item2Body',
                '— armazenada exclusivamente em forma de hash irreversível com o algoritmo Argon2 (estado da arte em segurança de senhas). Nós nunca temos acesso à sua senha em texto claro.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section2Item3Label', 'Conteúdo criado na plataforma')}</strong>{' '}
              {t(
                'privacy.section2Item3Body',
                '— os textos, manuscritos, esboços, notas e demais materiais que você produz ou importa no editor. Esses dados são usados para fornecer as funcionalidades de IA e salvar seu progresso.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section2Item4Label', 'Dados de uso e telemetria')}</strong>{' '}
              {t(
                'privacy.section2Item4Body',
                '— informações sobre como você interage com a plataforma (páginas visitadas, funcionalidades usadas, tempo de sessão) para melhorar a experiência do produto.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section2Item5Label', 'Dados de pagamento')}</strong>{' '}
              {t(
                'privacy.section2Item5Body1',
                '— valores, datas e status das transações. Os dados de cartão ou método de pagamento são processados diretamente pelo nosso parceiro Asaas e'
              )}{' '}
              <em>{t('privacy.section2Item5Emphasis', 'nunca')}</em>{' '}
              {t('privacy.section2Item5Body2', 'armazenados em nossos servidores.')}
            </li>
          </ul>
        </section>

        {/* 3 — Base Legal */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section3Title', '3. Base Legal para o Tratamento (LGPD, Art. 7º)')}
          </h2>
          <p>
            {t(
              'privacy.section3Intro',
              'O tratamento dos seus dados pessoais é fundamentado nas seguintes bases legais previstas na Lei Geral de Proteção de Dados (Lei 13.709/2018):'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              <strong className="text-foreground">{t('privacy.section3Item1Label', 'Execução de contrato')}</strong>{' '}
              {t(
                'privacy.section3Item1Body',
                '(Art. 7º, V) — dados essenciais para criar sua conta, autenticar você na plataforma e entregar as funcionalidades contratadas (escrita com IA, exportação, etc.).'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section3Item2Label', 'Legítimo interesse')}</strong>{' '}
              {t(
                'privacy.section3Item2Body',
                '(Art. 7º, IX) — dados de uso e telemetria, para melhoria contínua do produto, prevenção de fraudes e segurança da plataforma.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section3Item3Label', 'Consentimento')}</strong>{' '}
              {t(
                'privacy.section3Item3Body',
                '(Art. 7º, I) — para comunicações de marketing e novidades sobre o produto, que você pode revogar a qualquer momento.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section3Item4Label', 'Cumprimento de obrigação legal')}</strong>{' '}
              {t(
                'privacy.section3Item4Body',
                '(Art. 7º, II) — quando exigido por legislação tributária, fiscal ou ordem judicial.'
              )}
            </li>
          </ul>
        </section>

        {/* 4 — Compartilhamento / Subprocessadores */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section4Title', '4. Compartilhamento com Operadores e Subprocessadores')}
          </h2>
          <p>
            {t(
              'privacy.section4Intro',
              'Não vendemos seus dados pessoais. Compartilhamos apenas o mínimo necessário com prestadores de serviço que atuam como operadores ou subprocessadores, listados abaixo:'
            )}
          </p>

          <div className="mt-4 space-y-5">
            {/* OpenAI */}
            <div className="rounded-xl border border-border p-5 space-y-2">
              <h3 className="text-base font-semibold text-foreground">OpenAI, L.L.C.</h3>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4OpenaiFinalidadeLabel', 'Finalidade:')}</strong>{' '}
                {t(
                  'privacy.section4OpenaiFinalidadeBody',
                  'Fornecimento das funcionalidades de inteligência artificial — geração de texto, sugestões de escrita, transcrição de áudio via Whisper e geração de embeddings para a Story Bible. Os trechos de texto selecionados ou gerados durante o uso do editor são enviados à API da OpenAI (família GPT-5) para processamento.'
                )}
              </p>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4OpenaiDadosLabel', 'Dados compartilhados:')}</strong>{' '}
                {t(
                  'privacy.section4OpenaiDadosBody',
                  'Extratos de texto do seu manuscrito conforme necessário para cada requisição de IA (não seu conteúdo completo de forma permanente).'
                )}
              </p>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4OpenaiPolicyLabel', 'Política de privacidade:')}</strong>{' '}
                <a
                  href="https://openai.com/policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4"
                >
                  openai.com/policies/privacy-policy
                </a>
              </p>
            </div>

            {/* Asaas */}
            <div className="rounded-xl border border-border p-5 space-y-2">
              <h3 className="text-base font-semibold text-foreground">Asaas Gestão Financeira S.A.</h3>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4AsaasFinalidadeLabel', 'Finalidade:')}</strong>{' '}
                {t(
                  'privacy.section4AsaasFinalidadeBody',
                  'Processamento de pagamentos, gestão de assinaturas, emissão de cobranças e gestão do plano contratado.'
                )}
              </p>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4AsaasDadosLabel', 'Dados compartilhados:')}</strong>{' '}
                {t(
                  'privacy.section4AsaasDadosBody',
                  'Nome, e-mail, CPF/CNPJ (quando exigido para nota fiscal) e dados da transação financeira. Dados de cartão são coletados e armazenados exclusivamente pelo Asaas.'
                )}
              </p>
              <p className="text-sm">
                <strong className="text-foreground">{t('privacy.section4AsaasPolicyLabel', 'Política de privacidade:')}</strong>{' '}
                <a
                  href="https://www.asaas.com/politica-de-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4"
                >
                  asaas.com/politica-de-privacidade
                </a>
              </p>
            </div>

            {/* Hosting */}
            <div className="rounded-xl border border-border p-5 space-y-2">
              <h3 className="text-base font-semibold text-foreground">
                {t('privacy.section4HostingTitle', 'Infraestrutura de Hospedagem (VPS próprio)')}
              </h3>
              <p className="text-sm">
                {t(
                  'privacy.section4HostingBody',
                  'Os servidores da reformu.lat são operados em um VPS próprio. Todos os dados de conta e conteúdo são armazenados nessa infraestrutura, com acesso restrito e protocolos de segurança.'
                )}
              </p>
            </div>
          </div>
        </section>

        {/* 5 — Transferência Internacional */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section5Title', '5. Transferência Internacional de Dados')}
          </h2>
          <p>
            {t(
              'privacy.section5Body1',
              'Ao utilizar as funcionalidades de inteligência artificial da plataforma, extratos do seu conteúdo são enviados à'
            )}{' '}
            <strong className="text-foreground">OpenAI, L.L.C.</strong>
            {t('privacy.section5Body2', ', empresa sediada nos')}{' '}
            <strong className="text-foreground">{t('privacy.section5CountryLabel', 'Estados Unidos da América')}</strong>.
            {' '}
            {t(
              'privacy.section5Body3',
              'Essa transferência internacional é realizada com base na execução do contrato de serviço que você aceitou e nas cláusulas contratuais padrão aplicáveis (Standard Contractual Clauses — SCCs), conforme a política da OpenAI.'
            )}
          </p>
          <p className="mt-3">
            {t(
              'privacy.section5Body4',
              'Os demais dados pessoais são armazenados e processados em servidores situados no Brasil ou em território com legislação de proteção de dados equivalente.'
            )}
          </p>
        </section>

        {/* 6 — Retenção */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section6Title', '6. Retenção de Dados')}
          </h2>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              {t(
                'privacy.section6Item1',
                'Dados de conta e conteúdo são mantidos enquanto sua conta estiver ativa ou conforme necessário para a prestação do serviço.'
              )}
            </li>
            <li>
              {t('privacy.section6Item2Body1', 'Após o encerramento da conta, dados pessoais são excluídos em até')}{' '}
              <strong className="text-foreground">{t('privacy.section6Item2Days', '90 dias')}</strong>,
              {' '}
              {t(
                'privacy.section6Item2Body2',
                'salvo obrigação legal de retenção (ex.: registros fiscais pelo prazo exigido por lei).'
              )}
            </li>
            <li>
              {t('privacy.section6Item3Body1', 'Logs de segurança e acesso são retidos por até')}{' '}
              <strong className="text-foreground">{t('privacy.section6Item3Months', '12 meses')}</strong>
              {' '}{t('privacy.section6Item3Body2', 'para fins de auditoria e prevenção a fraudes.')}
            </li>
          </ul>
        </section>

        {/* 7 — Segurança */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section7Title', '7. Segurança')}
          </h2>
          <p>
            {t(
              'privacy.section7Intro',
              'Adotamos medidas técnicas e organizacionais adequadas para proteger seus dados contra acesso não autorizado, perda, alteração ou divulgação indevida:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              {t('privacy.section7Item1Body1', 'Senhas armazenadas com hashing')}{' '}
              <strong className="text-foreground">Argon2id</strong>,
              {' '}
              {t(
                'privacy.section7Item1Body2',
                'algoritmo resistente a ataques de força bruta e de hardware especializado.'
              )}
            </li>
            <li>
              {t('privacy.section7Item2Body', 'Comunicações entre seu dispositivo e nossos servidores protegidas por')}{' '}
              <strong className="text-foreground">TLS 1.2+</strong>.
            </li>
            <li>
              {t(
                'privacy.section7Item3',
                'Chaves de criptografia BYOK (Bring Your Own Key) protegidas com criptografia em repouso para usuários que utilizam esse recurso.'
              )}
            </li>
            <li>
              {t(
                'privacy.section7Item4',
                'Acesso aos sistemas de produção restrito por autenticação multifator e princípio do menor privilégio.'
              )}
            </li>
          </ul>
          <p className="mt-3">
            {t(
              'privacy.section7Closing',
              'Nenhum sistema é 100% infalível. Em caso de incidente de segurança que afete seus dados, notificaremos você e a Autoridade Nacional de Proteção de Dados (ANPD) nos prazos previstos pela LGPD.'
            )}
          </p>
        </section>

        {/* 8 — Direitos do Titular */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section8Title', '8. Seus Direitos como Titular (LGPD, Art. 18)')}
          </h2>
          <p>
            {t(
              'privacy.section8Intro',
              'Nos termos da Lei Geral de Proteção de Dados, você tem os seguintes direitos em relação aos seus dados pessoais:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              <strong className="text-foreground">{t('privacy.section8Item1Label', 'Acesso')}</strong>{' '}
              {t(
                'privacy.section8Item1Body',
                '— solicitar confirmação da existência de tratamento e obter uma cópia dos dados que mantemos sobre você.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item2Label', 'Correção')}</strong>{' '}
              {t(
                'privacy.section8Item2Body',
                '— solicitar a atualização ou retificação de dados incompletos, inexatos ou desatualizados.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item3Label', 'Anonimização, bloqueio ou eliminação')}</strong>{' '}
              {t(
                'privacy.section8Item3Body',
                '— de dados desnecessários, excessivos ou tratados em desconformidade com a LGPD.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item4Label', 'Portabilidade')}</strong>{' '}
              {t(
                'privacy.section8Item4Body',
                '— receber seus dados em formato estruturado e legível por máquina para transferência a outro serviço.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item5Label', 'Eliminação')}</strong>{' '}
              {t(
                'privacy.section8Item5Body',
                '— solicitar a exclusão dos dados tratados com base no seu consentimento.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item6Label', 'Revogação do consentimento')}</strong>{' '}
              {t(
                'privacy.section8Item6Body',
                '— retirar consentimentos previamente concedidos a qualquer momento, sem prejuízo da licitude do tratamento anterior.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item7Label', 'Informação sobre compartilhamento')}</strong>{' '}
              {t(
                'privacy.section8Item7Body',
                '— saber com quais entidades públicas e privadas compartilhamos seus dados.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section8Item8Label', 'Revisão de decisões automatizadas')}</strong>{' '}
              {t(
                'privacy.section8Item8Body',
                '— solicitar revisão de decisões tomadas exclusivamente com base em tratamento automatizado.'
              )}
            </li>
          </ul>
          <p className="mt-4">
            {t('privacy.section8ContactBody1', 'Para exercer qualquer um desses direitos, envie um e-mail para')}{' '}
            <a href="mailto:diegosierravibes@gmail.com" className="text-primary underline underline-offset-4">
              diegosierravibes@gmail.com
            </a>{' '}
            {t('privacy.section8ContactBody2', 'com o assunto "LGPD — [Direito solicitado]". Responderemos em até')}{' '}
            <strong className="text-foreground">{t('privacy.section8ContactDays', '15 dias corridos')}</strong>.
          </p>
        </section>

        {/* 9 — Cookies e Sessões */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section9Title', '9. Cookies e Sessões')}
          </h2>
          <p>
            {t('privacy.section9Intro', 'A plataforma utiliza os seguintes mecanismos de identificação:')}
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>
              <strong className="text-foreground">{t('privacy.section9Item1Label', 'Cookie de sessão (web)')}</strong>{' '}
              {t(
                'privacy.section9Item1Body',
                '— cookie seguro e HttpOnly usado exclusivamente para manter você autenticado enquanto navega na plataforma. Expira ao fechar o navegador ou após o período de inatividade configurado.'
              )}
            </li>
            <li>
              <strong className="text-foreground">{t('privacy.section9Item2Label', 'Bearer token (app nativo Android)')}</strong>{' '}
              {t(
                'privacy.section9Item2Body',
                '— token de autenticação armazenado localmente no dispositivo, usado para autenticar requisições à API a partir do aplicativo instalado.'
              )}
            </li>
          </ul>
          <p className="mt-3">
            {t(
              'privacy.section9Closing',
              'Não utilizamos cookies de rastreamento de terceiros, pixels de publicidade ou ferramentas de analytics que transmitam dados a redes de anunciantes.'
            )}
          </p>
        </section>

        {/* 10 — Alterações */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section10Title', '10. Alterações nesta Política')}
          </h2>
          <p>
            {t(
              'privacy.section10Body',
              'Podemos atualizar esta Política de Privacidade periodicamente. Alterações significativas serão comunicadas por e-mail ou mediante aviso em destaque na plataforma com antecedência mínima de 30 dias. O uso continuado do serviço após a entrada em vigor das alterações implica aceitação da nova versão.'
            )}
          </p>
        </section>

        {/* 11 — Contato */}
        <section>
          <h2 className="text-foreground text-xl font-semibold mt-8 mb-3">
            {t('privacy.section11Title', '11. Contato e Encarregado de Dados (DPO)')}
          </h2>
          <p>
            {t(
              'privacy.section11Intro',
              'Para exercer seus direitos, esclarecer dúvidas ou registrar reclamações sobre o tratamento dos seus dados, entre em contato com nosso encarregado de dados:'
            )}
          </p>
          <div className="mt-4 rounded-xl border border-border p-5 space-y-1">
            <p className="text-sm">
              <strong className="text-foreground">{t('privacy.section11EmailLabel', 'E-mail:')}</strong>{' '}
              <a href="mailto:diegosierravibes@gmail.com" className="text-primary underline underline-offset-4">
                diegosierravibes@gmail.com
              </a>
            </p>
            <p className="text-sm text-muted-foreground/70">
              {t('privacy.section11ResponseTime', 'Tempo de resposta: até 15 dias corridos.')}
            </p>
          </div>
          <p className="mt-4 text-sm">
            {t('privacy.section11ClosingBody1', 'Você também pode apresentar reclamação diretamente à')}{' '}
            <strong className="text-foreground">{t('privacy.section11AnpdLabel', 'Autoridade Nacional de Proteção de Dados (ANPD)')}</strong>{' '}
            {t('privacy.section11ClosingBody2', 'pelo portal')}{' '}
            <a
              href="https://www.gov.br/anpd"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-4"
            >
              gov.br/anpd
            </a>.
          </p>
        </section>

      </div>
    </MarketingLayout>
  );
}
